import merge from 'lodash/merge';
import { createI18n, type I18nOptions } from 'vue-i18n';
import en from '@/locales/en.json';
import es from '@/locales/es.json';
import pt from '@/locales/pt.json';
import type { LanguageCode } from '@/interfaces/user.interface';

/**
 * Each product keeps its translations in `src/modules/<product>/locales`,
 * discovered by filename like `bu.config.ts`, so nothing here names a product.
 */
const moduleLocales = import.meta.glob<Record<string, unknown>>('../modules/*/locales/*.json', {
  eager: true,
  import: 'default',
});

const withModuleLocales = <T extends object>(code: string, shared: T): T =>
  merge(
    {},
    shared,
    ...Object.keys(moduleLocales)
      .filter((path) => path.endsWith(`/${code}.json`))
      .map((path) => moduleLocales[path]),
  );

export const messages = {
  es: withModuleLocales('es', es),
  en: withModuleLocales('en', en),
  pt: withModuleLocales('pt', pt),
};

export type i18nMessageSchema = typeof es;
export const i18n = createI18n<[i18nMessageSchema], 'en' | 'es' | 'pt'>({
  legacy: false,
  locale: navigator.language.split('-')[0],
  fallbackLocale: 'es',
  silentTranslationWarn: true,
  warnHtmlMessage: false,
  fallbackWarn: false,
  missingWarn: false,
  messages: {
    es: messages.es,
    //@ts-ignore
    en: messages.en,
    //@ts-ignore
    pt: messages.pt,
  },
} satisfies I18nOptions);

export function setLocale(code: LanguageCode) {
  ((i18n.global as any).locale as any).value = code;
}

export async function setLocaleByLanguageCode(code: LanguageCode) {
  setLocale(code as LanguageCode);
}
